import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Select from "react-select";
import Table from "../../core/pagination/datatable";
import TooltipIcons from "../../core/common/tooltip-content/tooltipIcons";
import RefreshIcon from "../../core/common/tooltip-content/refresh";
import CollapesIcon from "../../core/common/tooltip-content/collapes";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import CommonFooter from "../../core/common/footer/commonFooter";
import { toast } from "react-toastify";

import { getSalesReport } from "../../api/salesReportApi";
import { getAllStores } from "../../api/storeApi";
import { fetchProductList } from "../../api/productApi";

const DEFAULT_PRODUCT_IMAGE = `${process.env.PUBLIC_URL || ""}/assets/img/by-default.png`;

const IMAGE_BASE = (
  process.env.REACT_APP_API_IMAGE_BASE_URL ||
  "https://hamilton-shop-images.s3.ap-south-1.amazonaws.com"
).replace(/\/+$/, "");

// normalize key
const normalizeKey = (v = "") =>
  typeof v === "string"
    ? v.replace(/^\/+/, "").replace(/^catalog\//i, "catalog/")
    : "";

const isAbsoluteHttp = (url = "") => /^https?:\/\//i.test(url);
const isDataUrl = (url = "") => /^data:image/i.test(url);

const resolveImageUrl = (raw) => {
  if (!raw) return DEFAULT_PRODUCT_IMAGE;

  const baseVal = Array.isArray(raw) ? raw[0] : raw;
  const val =
    typeof baseVal === "object" && baseVal !== null
      ? baseVal.url || baseVal.key || baseVal.path || baseVal.location || baseVal.src || ""
      : baseVal;

  if (isDataUrl(val)) return val;
  if (isAbsoluteHttp(val)) return val;

  const key = normalizeKey(val);
  return `${IMAGE_BASE}/${key}`;
};

const SalesReport = () => {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({
    totalProducts: 0,
    totalSoldQty: 0,
    totalSoldAmount: 0,
    totalInstockQty: 0,
  });

  const [loading, setLoading] = useState(false);
  const [storeOptions, setStoreOptions] = useState([]);
  const [productOptions, setProductOptions] = useState([]);

  const [filters, setFilters] = useState({
    dateFrom: null,
    dateTo: null,
    vendorStoreId: localStorage.getItem("storeId") || "",
    productId: "",
    search: "",
  });

  const [submittedFilters, setSubmittedFilters] = useState({
    dateFrom: null,
    dateTo: null,
    vendorStoreId: localStorage.getItem("storeId") || "",
    productId: "",
    search: "",
  });

  const formatMoney = (value) => `$${Number(value || 0).toFixed(2)}`;

  const loadFilterOptions = async () => {
    try {
      const [storesRes, productsRes] = await Promise.all([
        getAllStores ? getAllStores() : Promise.resolve([]),
        fetchProductList ? fetchProductList() : Promise.resolve([]),
      ]);

      const stores = Array.isArray(storesRes?.data)
        ? storesRes.data
        : Array.isArray(storesRes)
          ? storesRes
          : [];

      const products = Array.isArray(productsRes?.data)
        ? productsRes.data
        : Array.isArray(productsRes)
          ? productsRes
          : [];

      setStoreOptions([
        { value: "", label: window.translate("All Stores") },
        ...stores.map((store) => ({
          value: store.vendorStoreId,
          label:
            store?.storeName?.en ||
            store?.storeName?.ar ||
            store?.name ||
            store?.email ||
            window.translate("Store"),
        })),
      ]);

      setProductOptions([
        { value: "", label: window.translate("All Products") },
        ...products.map((product) => ({
          value: product._id || product.id,
          label:
            product?.productName?.en ||
            product?.productName?.ar ||
            product?.name ||
            product?.sku ||
            window.translate("Product"),
        })),
      ]);
    } catch (error) {
      console.error("loadFilterOptions error:", error);
    }
  };

  const fetchReport = async (customFilters = submittedFilters) => {
    try {
      setLoading(true);

      const params = {
        page: 1,
        limit: 100,
        ...(customFilters.vendorStoreId
          ? { vendorStoreId: customFilters.vendorStoreId }
          : {}),
        ...(customFilters.productId
          ? { productId: customFilters.productId }
          : {}),
        ...(customFilters.search ? { search: customFilters.search } : {}),
        ...(customFilters.dateFrom
          ? { dateFrom: dayjs(customFilters.dateFrom).format("YYYY-MM-DD") }
          : {}),
        ...(customFilters.dateTo
          ? { dateTo: dayjs(customFilters.dateTo).format("YYYY-MM-DD") }
          : {}),
      };

      const response = await getSalesReport(params);

      setRows(Array.isArray(response?.data) ? response.data : []);
      setSummary(
        response?.summary || {
          totalProducts: 0,
          totalSoldQty: 0,
          totalSoldAmount: 0,
          totalInstockQty: 0,
        },
      );
    } catch (error) {
      toast.error(error?.message || window.translate("Failed to fetch sales report"));
      setRows([]);
      setSummary({
        totalProducts: 0,
        totalSoldQty: 0,
        totalSoldAmount: 0,
        totalInstockQty: 0,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFilterOptions();
    fetchReport(submittedFilters);
  }, []);

  const handleGenerateReport = async (e) => {
    e.preventDefault();
    setSubmittedFilters(filters);
    fetchReport(filters);
  };

  const handleRefresh = () => {
    fetchReport(submittedFilters);
  };

  const columns = [
    {
      title: window.translate("Product Name"),
      dataIndex: "productName",
      render: (text, record) => {
        const imageSrc = resolveImageUrl(record.image);

        return (
          <span className="productimgname">
            <Link to="#" className="product-img stock-img">
              <img
                alt={text}
                src={imageSrc}
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = DEFAULT_PRODUCT_IMAGE;
                }}
              />
            </Link>
            <Link to="#">{text || window.translate("N/A")}</Link>
          </span>
        );
      },
      sorter: (a, b) =>
        String(a.productName || "").localeCompare(String(b.productName || "")),
    },
    {
      title: window.translate("SKU"),
      dataIndex: "sku",
      sorter: (a, b) => String(a.sku || "").localeCompare(String(b.sku || "")),
    },
    {
      title: window.translate("Category"),
      dataIndex: "category",
      sorter: (a, b) =>
        String(a.category || "").localeCompare(String(b.category || "")),
    },
    {
      title: window.translate("Brand"),
      dataIndex: "brand",
      sorter: (a, b) =>
        String(a.brand || "").localeCompare(String(b.brand || "")),
    },
    {
      title: window.translate("Sold Qty"),
      dataIndex: "soldQty",
      render: (value) => Number(value || 0),
      sorter: (a, b) => Number(a.soldQty || 0) - Number(b.soldQty || 0),
    },
    {
      title: window.translate("Sold Amount"),
      dataIndex: "soldAmount",
      render: (value) => formatMoney(value),
      sorter: (a, b) => Number(a.soldAmount || 0) - Number(b.soldAmount || 0),
    },
    {
      title: window.translate("Instock Qty"),
      dataIndex: "instockQty",
      render: (value) => Number(value || 0),
      sorter: (a, b) => Number(a.instockQty || 0) - Number(b.instockQty || 0),
    },
  ];

  const reportTitle = window.translate("Sales Report");

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>{window.translate("Sales Report")}</h4>
              <h6>{window.translate("Manage your Sales report")}</h6>
            </div>
          </div>
          <ul className="table-top-head">
            <li>
              <Link to="#" onClick={handleRefresh}>
                <RefreshIcon />
              </Link>
            </li>
            <CollapesIcon />
          </ul>
        </div>

        <div className="row">
          <div className="col-xl-3 col-sm-6 col-12 d-flex">
            <div className="card border border-success sale-widget flex-fill">
              <div className="card-body d-flex align-items-center">
                <span className="sale-icon bg-success text-white">
                  <i className="ti ti-align-box-bottom-left-filled fs-24" />
                </span>
                <div className="ms-2">
                  <p className="fw-medium mb-1">{window.translate("Total Sold Amount")}</p>
                  <div>
                    <h3>{formatMoney(summary.totalSoldAmount)}</h3>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-xl-3 col-sm-6 col-12 d-flex">
            <div className="card border border-info sale-widget flex-fill">
              <div className="card-body d-flex align-items-center">
                <span className="sale-icon bg-info text-white">
                  <i className="ti ti-shopping-cart fs-24" />
                </span>
                <div className="ms-2">
                  <p className="fw-medium mb-1">{window.translate("Total Sold Qty")}</p>
                  <div>
                    <h3>{Number(summary.totalSoldQty || 0)}</h3>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-xl-3 col-sm-6 col-12 d-flex">
            <div className="card border border-orange sale-widget flex-fill">
              <div className="card-body d-flex align-items-center">
                <span className="sale-icon bg-orange text-white">
                  <i className="ti ti-package fs-24" />
                </span>
                <div className="ms-2">
                  <p className="fw-medium mb-1">{window.translate("Total Products")}</p>
                  <div>
                    <h3>{Number(summary.totalProducts || 0)}</h3>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-xl-3 col-sm-6 col-12 d-flex">
            <div className="card border border-danger sale-widget flex-fill">
              <div className="card-body d-flex align-items-center">
                <span className="sale-icon bg-danger text-white">
                  <i className="ti ti-alert-circle-filled fs-24" />
                </span>
                <div className="ms-2">
                  <p className="fw-medium mb-1">{window.translate("Total Instock Qty")}</p>
                  <div>
                    <h3>{Number(summary.totalInstockQty || 0)}</h3>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="card border-0">
          <div className="card-body pb-1">
            <form onSubmit={handleGenerateReport}>
              <div className="row align-items-end">
                <div className="col-lg-10">
                  <div className="row">
                    <div className="col-md-3">
                      <div className="mb-3">
                        <label className="form-label">{window.translate("From Date")}</label>
                        <div className="input-icon-start position-relative">
                          <DatePicker
                            className="form-control datetimepicker"
                            placeholder={window.translate("dd/mm/yyyy")}
                            value={
                              filters.dateFrom ? dayjs(filters.dateFrom) : null
                            }
                            onChange={(date) =>
                              setFilters((prev) => ({
                                ...prev,
                                dateFrom: date ? date.toDate() : null,
                              }))
                            }
                          />
                          <span className="input-icon-left">
                            <i className="ti ti-calendar" />
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="col-md-3">
                      <div className="mb-3">
                        <label className="form-label">{window.translate("To Date")}</label>
                        <div className="input-icon-start position-relative">
                          <DatePicker
                            className="form-control datetimepicker"
                            placeholder={window.translate("dd/mm/yyyy")}
                            value={
                              filters.dateTo ? dayjs(filters.dateTo) : null
                            }
                            onChange={(date) =>
                              setFilters((prev) => ({
                                ...prev,
                                dateTo: date ? date.toDate() : null,
                              }))
                            }
                          />
                          <span className="input-icon-left">
                            <i className="ti ti-calendar" />
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="col-md-3">
                      <div className="mb-3">
                        <label className="form-label">{window.translate("Store")}</label>
                        <Select
                          classNamePrefix="react-select"
                          options={storeOptions}
                          placeholder={window.translate("Choose")}
                          value={
                            storeOptions.find(
                              (item) => item.value === filters.vendorStoreId,
                            ) || null
                          }
                          onChange={(selected) =>
                            setFilters((prev) => ({
                              ...prev,
                              vendorStoreId: selected?.value || "",
                            }))
                          }
                        />
                      </div>
                    </div>

                    <div className="col-md-3">
                      <div className="mb-3">
                        <label className="form-label">{window.translate("Products")}</label>
                        <Select
                          classNamePrefix="react-select"
                          options={productOptions}
                          placeholder={window.translate("Choose")}
                          value={
                            productOptions.find(
                              (item) => item.value === filters.productId,
                            ) || null
                          }
                          onChange={(selected) =>
                            setFilters((prev) => ({
                              ...prev,
                              productId: selected?.value || "",
                            }))
                          }
                        />
                      </div>
                    </div>

                    <div className="col-md-12">
                      <div className="mb-3">
                        <label className="form-label">{window.translate("Search")}</label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder={window.translate(
                            "Search by product name, sku, category, brand",
                          )}
                          value={filters.search}
                          onChange={(e) =>
                            setFilters((prev) => ({
                              ...prev,
                              search: e.target.value,
                            }))
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-lg-2">
                  <div className="mb-3">
                    <button className="btn btn-primary w-100" type="submit">
                      {window.translate("Generate Report")}
                    </button>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>

        <div className="card table-list-card hide-search">
          <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
            <div>
              <h4>{reportTitle}</h4>
            </div>
            <ul className="table-top-head">
              <TooltipIcons />
              <li>
                <Link
                  to="#"
                  data-bs-toggle="tooltip"
                  data-bs-placement="top"
                  title={window.translate("Print")}
                >
                  <i className="ti ti-printer" />
                </Link>
              </li>
            </ul>
          </div>

          <div className="card-body">
            <div className="table-responsive">
              {loading ? (
                <div className="text-center py-4">{window.translate("Loading...")}</div>
              ) : (
                <Table columns={columns} dataSource={rows} />
              )}
            </div>
          </div>
        </div>
      </div>

      <CommonFooter />
    </div>
  );
};

export default SalesReport;
